import { useSetSearchParam } from '@/hooks/router';
import { PaginationInput } from 'gql-types';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';

type DynamicPaging = {
    page: number;
    setPage(page: number): void;
    pageSize: number;
    setPageSize: (pageSize: number) => void;
    pageSizeOptions: number[];
};

export const emptyPaginationByPage: PaginationByPage = {
    dynamicPaging: {
        page: 0,
        setPage() {
            // Do Nothing
        },
        pageSize: 25,
        setPageSize: () => {
            // Do Nothing
        },
        pageSizeOptions: [],
    },
    resetPage() {
        // Do Nothing
    },
    paginationInput: {
        offset: 0,
    },
    preloadNextPageInput: null,
    preloadPreviousPageInput: null,
};

export type PaginationByPage = {
    dynamicPaging: DynamicPaging;
    resetPage(): void;
    paginationInput: PaginationInput;
    // Next page input is null if there are no more pages
    preloadNextPageInput: PaginationInput | null;
    // Previous page input is null if the current page is the first page
    preloadPreviousPageInput: PaginationInput | null;
};

type DynamicPageSize = {
    containerRef?: HTMLElement;
    firstElement?: HTMLElement[];
};

type UsePaginationByPageOptions = {
    totalCount?: number;
    dynamicPageSize?: DynamicPageSize;
    defaultPageSize?: number;
    pageSizeOptions?: number[];
    withSearchParam?: boolean;
};

type UsePaginationByPage = (options?: UsePaginationByPageOptions) => PaginationByPage;

const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const getPage = (page?: string | null) => {
    try {
        if (page) {
            return Number.parseInt(page) - 1;
        }
        return 0;
    } catch {
        return 0;
    }
};

type UsePage = (
    withSearchParam?: boolean,
) => [page: number, setPage: Dispatch<SetStateAction<number>>];
const usePage: UsePage = (withSearchParam) => {
    const [pageSearchParam, setPageSearchParam] = useSetSearchParam('page');
    const [pageState, setPageState] = useState(getPage(pageSearchParam));
    const lastPageSearchParam = useRef(pageSearchParam);

    useEffect(() => {
        if (withSearchParam && pageSearchParam !== lastPageSearchParam.current) {
            lastPageSearchParam.current = pageSearchParam;
            setPageState(getPage(pageSearchParam));
        }
    }, [pageSearchParam, withSearchParam]);

    const setPage = useCallback(
        (page: SetStateAction<number>) => {
            setPageState((prev) => {
                const nextValue = typeof page === 'function' ? page(prev) : page;
                if (withSearchParam) {
                    const nextActualPage = nextValue + 1;
                    setPageSearchParam(nextActualPage === 1 ? null : `${nextActualPage}`);
                }
                return nextValue;
            });
        },
        [setPageSearchParam, withSearchParam],
    );

    return [pageState, setPage];
};

export const usePaginationByPage: UsePaginationByPage = (options): PaginationByPage => {
    const {
        totalCount,
        withSearchParam,
        defaultPageSize = DEFAULT_PAGE_SIZE,
        pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    } = options ?? {};
    const [page, setPage] = usePage(withSearchParam);
    const initialPageSize = pageSizeOptions.length
        ? pageSizeOptions.includes(defaultPageSize)
            ? defaultPageSize
            : pageSizeOptions[0]
        : DEFAULT_PAGE_SIZE;
    const [pageSize, setPageSizeInternal] = useState(initialPageSize);

    // When page size changes, adjust the page number to keep the same starting offset
    const setPageSize = useCallback(
        (newPageSize: number) => {
            const currentOffset = page * pageSize;
            const newPage = Math.floor(currentOffset / newPageSize);
            setPage(newPage);
            setPageSizeInternal(newPageSize);
        },
        [page, pageSize, setPage],
    );

    const preloadNextPageInput = useMemo((): PaginationInput | null => {
        const nextOffset = (page + 1) * pageSize;
        if (typeof totalCount === 'number' && nextOffset > totalCount) {
            return null;
        }
        return {
            limit: pageSize,
            offset: nextOffset,
        };
    }, [page, totalCount, pageSize]);

    const resetPage = useCallback(() => {
        setPage(0);
    }, [setPage]);

    const preloadPreviousPageInput = useMemo((): PaginationInput | null => {
        const previousOffset = (page - 1) * pageSize;
        if (previousOffset <= 0) {
            return null;
        }
        return {
            limit: pageSize,
            offset: previousOffset,
        };
    }, [page, pageSize]);

    const dynamicPaging = useMemo(
        (): DynamicPaging => ({
            page,
            setPage,
            pageSize,
            setPageSize,
            pageSizeOptions,
        }),
        [page, pageSize, pageSizeOptions, setPage, setPageSize],
    );

    const paginationInput = useMemo(
        () => ({
            limit: pageSize,
            offset: page * pageSize,
        }),
        [page, pageSize],
    );

    return {
        dynamicPaging,
        paginationInput,
        resetPage,
        preloadNextPageInput,
        preloadPreviousPageInput,
    };
};
